import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Bot, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Configures the auto-reply classifier that prevents WhatsApp Business
 * greetings (e.g. "Thank you for contacting…") from killing the outreach
 * sequence. Two signals — a time-window check and a phrase allowlist —
 * are tuneable here without a redeploy.
 */
export const SmbAutoReplyConfigCard = () => {
  const [doc, setDoc] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [timeWindow, setTimeWindow] = useState('60');
  const [phraseWindow, setPhraseWindow] = useState('300');
  const [phrasesText, setPhrasesText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/smb/outreach/auto-reply-config');
      if (res.data.success) {
        const d = res.data.data;
        setDoc(d);
        setEnabled(d.enabled !== false);
        setTimeWindow(String(d.time_window_sec ?? 60));
        setPhraseWindow(String(d.phrase_window_sec ?? 300));
        setPhrasesText((d.phrases || []).join('\n'));
      }
    } catch {
      toast.error('Failed to load auto-reply config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const tw = parseInt(timeWindow, 10);
    const pw = parseInt(phraseWindow, 10);
    if (!Number.isFinite(tw) || tw < 0 || tw > 3600) {
      toast.error('Time window must be between 0 and 3600 seconds'); return;
    }
    if (!Number.isFinite(pw) || pw < 0 || pw > 7200) {
      toast.error('Phrase window must be between 0 and 7200 seconds'); return;
    }
    const phrases = phrasesText.split('\n').map(s => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      const res = await api.put('/smb/outreach/auto-reply-config', {
        enabled,
        time_window_sec: tw,
        phrase_window_sec: pw,
        phrases,
      });
      if (res.data.success) {
        setDoc(res.data.data);
        toast.success('Auto-reply config saved');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removePhrase = (phrase) => {
    setPhrasesText(phrasesText.split('\n').filter(p => p.trim() !== phrase).join('\n'));
  };

  if (loading) {
    return (
      <Card data-testid="smb-auto-reply-config-card">
        <CardContent className="py-6 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…
        </CardContent>
      </Card>
    );
  }

  const previewPhrases = phrasesText.split('\n').map(s => s.trim()).filter(Boolean);

  return (
    <Card data-testid="smb-auto-reply-config-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><Bot className="h-4 w-4" />Auto-Reply Detection</CardTitle>
            <CardDescription>
              Detects WhatsApp Business greetings (e.g. &ldquo;Thank you for contacting&hellip;&rdquo;) and prevents them from stopping the outreach sequence. Two signals — time-window + phrase allowlist — combined.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label htmlFor="auto-reply-enabled" className="text-xs">{enabled ? 'Enabled' : 'Disabled'}</Label>
            <Switch
              id="auto-reply-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="auto-reply-enabled-toggle"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="time-window">Time-window (seconds)</Label>
            <Input
              id="time-window" type="number" min={0} max={3600}
              value={timeWindow}
              onChange={e => setTimeWindow(e.target.value)}
              disabled={!enabled}
              data-testid="auto-reply-time-window"
            />
            <p className="text-xs text-muted-foreground">Inbound within this many seconds of our outbound = auto-reply. <strong>60s</strong> is a sensible default — humans don&apos;t reply that fast.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phrase-window">Phrase window (seconds)</Label>
            <Input
              id="phrase-window" type="number" min={0} max={7200}
              value={phraseWindow}
              onChange={e => setPhraseWindow(e.target.value)}
              disabled={!enabled}
              data-testid="auto-reply-phrase-window"
            />
            <p className="text-xs text-muted-foreground">Outer window for phrase-matched inbounds — used when content matches but the timing is slightly delayed.</p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phrases">Auto-reply phrases (one per line)</Label>
          <Textarea
            id="phrases"
            value={phrasesText}
            onChange={e => setPhrasesText(e.target.value)}
            rows={8}
            placeholder={'thank you for contacting\nwe will get back to you\nout of office'}
            disabled={!enabled}
            data-testid="auto-reply-phrases"
          />
          <p className="text-xs text-muted-foreground">Case-insensitive substring match against the inbound text after stripping punctuation. Empty list disables phrase-matching but keeps the time-window check active.</p>
        </div>

        {previewPhrases.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide opacity-70">Active phrases ({previewPhrases.length})</Label>
            <div className="flex flex-wrap gap-1.5">
              {previewPhrases.map((p, i) => (
                <Badge
                  key={`${p}-${i}`} variant="secondary"
                  className="gap-1 pr-1"
                  data-testid={`auto-reply-phrase-${i}`}
                >
                  {p}
                  <button
                    type="button"
                    onClick={() => removePhrase(p)}
                    className="rounded-full hover:bg-background/40 p-0.5"
                    title="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {doc?.updated_at ? `Last updated ${new Date(doc.updated_at).toLocaleString()}` : 'Never saved'}
          </div>
          <Button onClick={save} disabled={saving} data-testid="auto-reply-save-btn">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
